import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { PageHeader } from './PageHeader';
import { HelpModal } from './HelpModal';
import { Footer } from './Footer';
import { WelcomeDesktopInstructions } from './WelcomeDesktopInstructions';
import { WelcomeMobileInstructions } from './WelcomeMobileInstructions';
import { trackScreenView } from '../utils/analytics';
import { SKIP_SIGNUP_FOR_LANGUAGE } from '../config/constants';
import { appendLanguageToEuFormUrl } from '../utils/urlEncoding';
import QRCode from 'qrcode';

interface WelcomeScreenProps {
  euFormUrl: string;
  onStart: () => void;
  userData?: {
    email: string;
    country: string;
    firstName?: string;
    lastName?: string;
  };
}

export function WelcomeScreen({ euFormUrl, onStart }: WelcomeScreenProps) {
  const { t, i18n } = useTranslation('welcome');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const hasTrackedScreenView = useRef(false);

  // Get consultation ID from root element
  const consultationId = (() => {
    const rootElement = document.getElementById('consultation-wizard-root');
    return rootElement?.getAttribute('data-consultation-id') || 'unknown';
  })();

  // Track screen view when component mounts (only once)
  useEffect(() => {
    if (!hasTrackedScreenView.current) {
      trackScreenView('welcome', consultationId, 1);
      hasTrackedScreenView.current = true;
    }
  }, [consultationId]);

  // Generate QR code on mount
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const currentUrl = new URL(window.location.href);
        const currentLang = (i18n.language || 'en').toLowerCase();
        
        // Check multiple sources for signup skip:
        // 1. URL parameter: ?skipSignup=true
        const params = new URLSearchParams(window.location.search);
        const urlParamSkip = params.get('skipSignup') === 'true';
        
        // 2. Data attribute: data-skip-signup="true"
        const rootElement = document.getElementById('consultation-wizard-root');
        const dataAttrSkip = rootElement?.getAttribute('data-skip-signup') === 'true';
        
        // 3. Environment variable (comma-separated language codes)
        const skipLanguages = SKIP_SIGNUP_FOR_LANGUAGE 
          ? SKIP_SIGNUP_FOR_LANGUAGE.split(',').map((lang: string) => lang.trim().toLowerCase())
          : [];
        const envSkip = skipLanguages.includes(currentLang);
        
        const shouldSkipSignup = urlParamSkip || dataAttrSkip || envSkip;
        const targetScreen = shouldSkipSignup ? 'howToUse' : 'signup';
        
        // Add screen parameter to URL for QR code
        currentUrl.searchParams.set('screen', targetScreen);
        const qrUrl = currentUrl.toString();
        
        const qrDataUrl = await QRCode.toDataURL(qrUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#131313',
            light: '#FFFFFF'
          }
        });
        setQrCodeUrl(qrDataUrl);
      } catch (err) {
        console.error('Error generating QR code:', err);
      }
    };
    generateQRCode();
  }, [i18n.language]);

  const handleOpenConsultation = () => {
    const currentLanguage = i18n.language || 'en';
    const urlWithLanguage = appendLanguageToEuFormUrl(euFormUrl, currentLanguage);
    window.open(urlWithLanguage, '_blank', 'noopener,noreferrer');
  };

  const handleEmailLink = () => {
    const subject = encodeURIComponent(t('emailLink.subject'));
    const body = encodeURIComponent(t('emailLink.body', { url: window.location.href }));
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <HelpModal 
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
      <PageHeader onHelpClick={() => setShowHelpModal(true)} />
      <main role="main" className="flex-1 w-full">
        <div className="max-w-[1800px] mx-auto px-8 lg:px-12 py-8 lg:py-12">
          <div className="text-center mb-12">
            <h1 className="mb-4 font-[Sofia_Sans_Condensed] text-[38px] font-bold leading-[1em]">{t('greeting')}</h1>
            <p className="max-w-2xl mx-auto">
              {t('description')}
            </p>
          </div>

          {/* Instructions Box */}
          <div className="bg-[#FFF5F2] border border-[#FFD4C7] mb-8 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#FF6F4E] flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-bold text-[20px]">{t('instructions.title')}</h2>
            </div>

            {/* Desktop Instructions - Hidden on screens 900px and below */}
            <div className="hidden lg:block">
              <WelcomeDesktopInstructions
                qrCodeUrl={qrCodeUrl}
                onOpenConsultation={handleOpenConsultation}
                onStart={onStart}
              />
            </div>

            {/* Mobile Instructions - Hidden on screens above 900px */}
            <div className="block lg:hidden">
              <WelcomeMobileInstructions
                onEmailLink={handleEmailLink}
                onOpenConsultation={handleOpenConsultation}
                onStart={onStart}
              />
            </div>
          </div>

          {/* Centered Deadline Text */}
          <div className="text-center mb-12">
            <p className="text-[16px] text-[#0A1402]">
              {t('deadline')}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
